import { useQuery, useQueryClient } from '@tanstack/react-query';
import { briefingApi, type BriefingResponse } from '../services/briefing-api';
import { dashboardApi } from '../services/dashboard-api';
import { formatNumber } from '@/shared/utils/number-format';

/**
 * Generează briefing local din datele de dashboard deja disponibile.
 * Folosit ca fallback când backend-ul nu e încă deployed sau când fetch-ul eșuează.
 */
async function buildLocalFallback(): Promise<BriefingResponse> {
  const now = new Date();
  const hour = now.getHours();
  const day = now.getDay();

  let context: BriefingResponse['context'] = 'morning';
  if (day === 0 || day === 6) context = 'weekend';
  else if (hour < 11) context = 'morning';
  else if (hour < 17) context = 'afternoon';
  else context = 'evening';

  const greetingMap: Record<string, string> = {
    morning: 'Bună dimineața.',
    afternoon: 'Salut.',
    evening: 'Bună seara.',
    weekend: 'Relaxează-te.',
  };

  const paragraphs: BriefingResponse['paragraphs'] = [];
  const actions: BriefingResponse['actions'] = [];

  try {
    const [stats, overdue] = await Promise.all([
      dashboardApi.stats().catch(() => null),
      dashboardApi.overdueInvoices().catch(() => []),
    ]);

    if (overdue && overdue.length > 0) {
      const total = overdue.reduce(
        (s: number, i: any) => s + Number(i.total || 0) - Number(i.paidAmount || 0),
        0,
      );
      const fmt = formatNumber(total, 0);
      paragraphs.push({
        text: 'Ai ' + overdue.length + ' ' + (overdue.length === 1 ? 'factură' : 'facturi') + ' restante, ' + fmt + ' RON de recuperat. Un reminder rapid ajută adesea.',
        entities: [],
      });
      actions.push({ id: 'see-overdue', label: 'Vezi restanțe', icon: 'chart', variant: 'secondary', href: '/facturi?filter=overdue' });
    }

    if (stats && stats.revenueThisMonth > 0) {
      const rev = formatNumber(Number(stats.revenueThisMonth), 0);
      paragraphs.push({
        text: 'Luna aceasta ai facturat ' + rev + ' RON, ' + stats.invoicesIssued + ' facturi către ' + stats.clientsActive + ' clienți activi.',
        entities: [],
      });
    }
  } catch {}

  if (paragraphs.length === 0) {
    paragraphs.push({
      text:
        context === 'weekend'
          ? 'Nimic urgent pentru weekend. Săptămâna viitoare începe proaspăt.'
          : 'Totul pare în ordine azi. Un moment bun pentru planificare pe termen lung.',
      entities: [],
    });
  }

  return {
    context,
    greeting: greetingMap[context] || 'Salut.',
    paragraphs,
    actions,
    generatedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + 60 * 60 * 1000).toISOString(),
    fromCache: false,
    source: 'fallback',
  };
}

export function useBriefing() {
  return useQuery({
    queryKey: ['dashboard', 'briefing'],
    queryFn: async () => {
      try {
        const data = await briefingApi.get();
        return { ...data, source: 'backend' as const };
      } catch {
        return buildLocalFallback();
      }
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
}

export function useRefreshBriefing() {
  const qc = useQueryClient();
  return async () => {
    // Use fetchQuery so isFetching state updates → button shows spinner
    await qc.fetchQuery({
      queryKey: ['dashboard', 'briefing'],
      queryFn: async () => {
        try {
          const data = await briefingApi.refresh();
          return { ...data, source: 'backend' as const };
        } catch {
          return buildLocalFallback();
        }
      },
      staleTime: 0,
    });
  };
}
